import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { previewOtasShadow } from "@/lib/otas-shadow.functions";

export const Route = createFileRoute("/_authenticated/admin/otas-shadow")({
  head: () => ({ meta: [{ title: "OTAS-01 Shadow Run | Admin" }, { name: "robots", content: "noindex" }] }),
  component: ShadowPage,
});

type Result = Awaited<ReturnType<typeof previewOtasShadow>>;

const fmt = (s: number) => new Date(s * 1000).toISOString().slice(0, 16).replace("T", " ");
const pct = (x: number | null) => (x === null ? "n/a" : `${(x * 100).toFixed(1)}%`);

function ShadowPage() {
  const run = useServerFn(previewOtasShadow);
  const [days, setDays] = useState<number | "today">(7);
  const [res, setRes] = useState<Result | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      setRes(
        await run({
          data: {
            fromS:
              days === "today"
                ? Math.floor(Date.now() / 86400000) * 86400
                : Math.floor(Date.now() / 1000) - days * 86400,
          },
        }),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Run failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">OTAS-01 · Shadow Run</p>
        <h1 className="font-display text-2xl font-bold">Weekly VWAP (live since 2026-10-09) versus the prior reference and the Grade A rejection wick candidate</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Read-only replay on live candles. No trades, no publication, no notifications. The live engine runs H4 SMA 50 dynamic allocation + with the A+ runner second target at 120 (Grade A unchanged at 70). Weekly VWAP (trade on the trend side of the weekly anchored VWAP) was adopted live on 2026-10-09; the second card keeps the prior reference without it. The third card adds a rejection wick filter on top of the live engine: Grade A entries require the M15 pullback candle to leave a wick of at least 33% of its range in the trade direction; A+ is unchanged. Under forward observation since 2026-10-13 (archive study: +110R vs +124R, drawdown 7R vs 11R). The daily VWAP variant was retired on 2026-10-05.
        </p>
      </div>

      <div className="rounded-lg border border-border/60 bg-card p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Dynamic Allocation Policy · adopted live 2026-09-30</p>
        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
          <li>Grade A+ (retrace 0.50 to 0.786 of the 3h H1 context range): uncapped, always admitted when qualified.</li>
          <li>Grade A: admitted only when every open position has reached TP1 and its stop sits at break even.</li>
          <li>Midweek shield: on Wednesday only Grade A+ is admitted. Grade A is blocked for the whole session.</li>
          <li>London open Judas buffer: no entry before 07:30 UTC, removing the first thirty minutes of the London open.</li>
          <li>Management unchanged: 20 stop, 40 first target with half close and stop to entry, 70 second target (Grade A+ runner second target at 120, adopted live 2026-10-05).</li>
        </ul>
        <p className="mt-3 font-mono text-[11px] text-muted-foreground">
          Three-year archive reference with both session constraints: 236 closed, WR 32.2%, net +53.24R, PF 1.51, max drawdown
          9.50R, versus the same policy without them at PF 1.36 and max drawdown 11.25R.
        </p>
      </div>


      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border/60 bg-card p-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Window</span>
        <button
          onClick={() => setDays("today")}
          className={`rounded-md px-3 py-1 text-xs ${days === "today" ? "bg-gold-muted text-gold" : "text-muted-foreground hover:text-foreground"}`}
        >
          Today
        </button>
        {[
          { d: 7, label: "7D" },
          { d: 14, label: "14D" },
          { d: 30, label: "30D" },
          { d: 60, label: "60D" },
          { d: 90, label: "90D" },
          { d: 180, label: "6M" },
          { d: 365, label: "12M" },
          { d: 1100, label: "3Y" },
        ].map(({ d, label }) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`rounded-md px-3 py-1 text-xs ${days === d ? "bg-gold-muted text-gold" : "text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </button>
        ))}
        <button onClick={go} disabled={busy} className="ml-auto flex items-center gap-2 rounded-md bg-gold px-4 py-1.5 text-xs font-semibold text-gold-foreground disabled:opacity-60">
          {busy && <Loader2 className="h-3 w-3 animate-spin" />} Run preview
        </button>
      </div>

      {err && <p className="text-sm text-destructive">{err}</p>}

      {res && (
        <>
          <p className="font-mono text-xs text-muted-foreground">
            {fmt(res.fromS)} to {fmt(res.lastBarS)} UTC · decisions {res.decisions} · opportunities {res.opportunities} · state{" "}
            {res.armed ? `ARMED from ${fmt(res.armedFromS!)}` : "NOT ARMED"}
          </p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {(() => {
              // Leader: highest Net R, ties broken by lower max drawdown.
              const leaderId = res.variants.length > 1
                ? [...res.variants].sort((a, b) => b.summary.netR - a.summary.netR || a.summary.maxDrawdownR - b.summary.maxDrawdownR)[0].summary.id
                : null;
              return res.variants.map(({ summary: s, trades, grades }) => {
              const lead = s.id === leaderId;
              const netCls = s.netR > 0 ? "text-emerald" : s.netR < 0 ? "text-destructive" : "";
              const rows: [string, ReactNode, boolean, string?][] = [
                ["Admitted", s.admitted, false],
                ["Regime rejected", s.regimeRejected, false],
                ["Capacity blocked", s.capacityBlocked, false],
                ["Session shield blocked", s.sessionBlocked, false],
                ["Closed / open", `${s.closed} / ${s.open}`, false],
                ["TP2 / TP1 BE / SL", `${s.tp2} / ${s.tp1Be} / ${s.sl}`, false],
                ["Win rate", pct(s.winRate), true],
                ["Net R", `${s.netR > 0 ? "+" : ""}${s.netR.toFixed(2)}`, true, netCls],
                ["Profit factor", s.profitFactor?.toFixed(2) ?? "n/a", true],
                ["Max drawdown R", s.maxDrawdownR.toFixed(2), true],
                ["Long / short R", `${s.longR.toFixed(2)} / ${s.shortR.toFixed(2)}`, true],
              ];
              return (
              <div key={s.id} className={`rounded-lg border bg-card p-4 ${lead ? "border-gold/60 ring-1 ring-gold/15" : "border-border/60"}`}>
                <div className="flex min-h-[3.75rem] items-start justify-between gap-2">
                  <p className="font-mono text-[10px] uppercase leading-5 tracking-widest text-gold">{s.label}</p>
                  {lead && (
                    <span className="shrink-0 rounded-sm border border-gold/50 bg-gold-muted px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-gold">
                      Lead Net R
                    </span>
                  )}
                </div>

                <dl className="mt-3 font-mono text-xs">
                  {rows.map(([k, v, core, cls], i) => (
                    <div key={k} className={`flex items-center justify-between rounded-sm px-2 py-1 ${i % 2 === 0 ? "bg-muted/40" : ""}`}>
                      <dt className={core ? "text-foreground/80" : "text-muted-foreground"}>{k}</dt>
                      <dd className={`text-right ${core ? "font-semibold" : ""} ${cls ?? ""}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border/60 pt-3">
                  {([["Grade A+", grades.aPlus], ["Grade A", grades.a]] as const).map(([label, g]) => (
                    <div key={label} className="font-mono text-[11px]">
                      <p className="text-[10px] uppercase tracking-widest text-gold">{label}</p>
                      <p className="mt-1 text-muted-foreground">Trades {g.admitted} · closed {g.closed}</p>
                      <p>WR {pct(g.winRate)} · Net {g.netR.toFixed(2)}R</p>
                      <p>PF {g.profitFactor?.toFixed(2) ?? "n/a"} · DD {g.maxDrawdownR.toFixed(2)}R</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 max-h-64 overflow-auto border-t border-border/60 pt-2">
                  {trades.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No simulated entries in this window.</p>
                  ) : (
                    <table className="w-full font-mono text-[11px]">
                      <tbody>
                        {trades.map((t) => (
                          <tr key={t.entryTime} className="border-b border-border/30">
                            <td className="py-1">{fmt(t.entryTime)}</td>
                            <td>{t.side}</td>
                            <td className="text-gold">{t.grade === "A_PLUS" ? "A+" : "A"}</td>
                            <td className="text-right">{t.entryQuote.toFixed(2)}</td>
                            <td className="text-right">{t.status}</td>
                            <td className="text-right">{t.grossR.toFixed(2)}R</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              );
              });
            })()}
          </div>
        </>
      )}
    </div>
  );
}
